import React from 'react';
import T from 'i18n';
import { Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { setHours, setMinutes, setSeconds, setMilliseconds, parse, isValid } from 'date-fns';
import logger from 'utils/logger';
import { LocalizationProvider } from '@mui/x-date-pickers';
import DatePicker, { isValidDate } from './DatePicker';

export default ({
  filterCategory,
  setFilters,
  label,
  fieldPrefix = 'date',
}: {
  filterCategory: null | any;
  setFilters: (newFilterCategory: any) => void;
  label?: string;
  fieldPrefix?: string;
}) => {
  const fromField = fieldPrefix === 'date' ? 'dateFrom' : `${fieldPrefix}From`;
  const toField = fieldPrefix === 'date' ? 'dateTo' : `${fieldPrefix}To`;
  const filterField = fieldPrefix === 'date' ? 'dateFilter' : `${fieldPrefix}Filter`;

  const updateDateValue = (field: string, newValue: any) => {
    if (!newValue) {
      setFilters({
        ...filterCategory,
        [field]: null,
        [filterField]: !!(field === fromField ? filterCategory?.[toField] : filterCategory?.[fromField]),
      });
      return;
    }

    if (!isValidDate(newValue)) return;

    const dateWithTimeReset = newValue
      ? setMilliseconds(setSeconds(setMinutes(setHours(newValue, 0), 0), 0), 0)
      : newValue;

    setFilters({
      ...filterCategory,
      [field]: dateWithTimeReset,
      [filterField]: !!(
        (field === fromField ? filterCategory?.[toField] : filterCategory?.[fromField]) ||
        dateWithTimeReset
      ),
    });
  };

  const handleUserTypedDate = (field: any) => (event: any) => {
    const typedDate = event.target.value;
    const parsedDate = parse(typedDate, 'dd/MM/yyyy', new Date());

    if (isValid(parsedDate)) {
      updateDateValue(field, parsedDate);
    } else {
      logger.ERROR('Invalid date format.');
    }
  };

  const handleDateChange = (field: string) => (newValue: any) => {
    updateDateValue(field, newValue);
  };

  const toDatePickerRef = React.useRef<HTMLInputElement>(null);

  const handleFromDateKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Tab' && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation(); // Prevent the event from bubbling up
      // Focus the "To" date picker
      if (toDatePickerRef.current) {
        toDatePickerRef.current.focus();
      }
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Grid xs={3}>
        <Typography
          style={{
            marginLeft: 0,
            alignSelf: 'center',
            marginTop: '0.5rem',
            fontWeight: 'bold',
          }}
        >
          { label || T.translate('filters.date')}
        </Typography>
      </Grid>
      <Grid xs={9}>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          <DatePicker
            value={filterCategory?.[fromField] && new Date(filterCategory?.[fromField])}
            onChange={handleDateChange(fromField)}
            onBlur={handleUserTypedDate(fromField)}
            placeholder={T.translate('generic.from')}
            maxDate={filterCategory?.[toField] ? new Date(filterCategory?.[toField]) : undefined}
            slotProps={{
              textField: {
                onKeyDown: handleFromDateKeyDown,
              },
            }}
          />
          <DatePicker
            value={filterCategory?.[toField] && new Date(filterCategory?.[toField])}
            onChange={handleDateChange(toField)}
            onBlur={handleUserTypedDate(toField)}
            placeholder={T.translate('generic.to')}
            style={{ marginLeft: '1rem' }}
            minDate={filterCategory?.[fromField] ? new Date(filterCategory?.[fromField]) : undefined}
            slotProps={{
              textField: {
                inputRef: toDatePickerRef,
              },
            }}
          />
        </div>
      </Grid>
    </LocalizationProvider>
  );
};
